import { useQuery } from "@tanstack/react-query";
import {
  Button as AntButton,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Select,
  Slider,
  Switch,
} from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  createMagiccode,
  getAllRewards,
  getSchemas,
} from "../actions/acc_actions";
import Button from "../components/Button"; // Custom Button
import ContentLayout from "../components/layout/ContentLayout";
import TagsInputItem from "../components/TagsInputItem";

export default function MagiccodeForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const businessSlug = useSelector((state) => state.account.businessSlug);
  const businessUrn = useSelector((state) => state.account.businessUrn);
  const accUrn = useSelector((state) => state.account.urn);

  const { data: rewardsData } = useQuery({
    queryKey: ["rewards", businessUrn],
    queryFn: () => getAllRewards(businessUrn),
    enabled: !!businessUrn,
  });

  const { data: schemasData } = useQuery({
    queryKey: ["schemas", businessUrn],
    queryFn: () => getSchemas(businessUrn),
    enabled: !!businessUrn,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState("voucher");
  const [tags, setTags] = useState([]);

  const handleTagsChange = (newTags) => {
    setTags(newTags);
  };

  const handleSubmit = (values) => {
    const formData = {
      accountUrn: accUrn,
      effects: [
        {
          type: selected?.toUpperCase(),
          businessSlug,
          schemeSlug: values.schemeSlug,
          rewardTypeSlug: values.rewardTypeSlug,
          quantity: values.quantity,
          summary: values.summary,
          multiUse: values.multiUse,
          validFrom: values.validFrom?.format("YYYY-MM-DD"),
          validUntil: values.validUntil?.format("YYYY-MM-DD"),
          lifetimeMode: "fixed",
        },
      ],
      message: values.message,
      numberOfSecrets: values.numberOfSecrets,
      claimableFrom: values.claimableFrom?.format("YYYY-MM-DD"),
      claimableUntil: values.claimableUntil?.format("YYYY-MM-DD"),
      maxClaimsPerUser: values.maxClaimsPerUser,
      maxClaimsPerCode: values.maxClaimsPerCode,
      name: values.name,
      notes: values.notes,
      tags,
    };

    setIsLoading(true);
    createMagiccode(accUrn, formData)
      .then(() => {
        navigate("/magic-codes");
        message.success(t("magic_code_created_success"));
      })
      .catch(() => {
        message.error(t("something_went_wrong"));
      });
    setIsLoading(false);
  };

  return (
    <ContentLayout
      header={
        <>
          <h1>
            {t("build_a_new")} <span>{t("code_batch")}</span>
          </h1>
          <p className="content_layout-subtitle">
            {t("create_your_magic_codes")}
          </p>
        </>
      }
    >
      <Form
        onFinish={handleSubmit}
        layout="vertical"
        className="rewards-form magiccode-form"
      >
        {/* Type Section */}
        <div className="rewards-form-section">
          <h4>{t("type")}</h4>
          <div className="rewards-inputs">
            <Form.Item>
              <div className="form-row radio-btns">
                <Button
                  variant={selected === "voucher" ? "primary" : "secondary"}
                  onClick={() => setSelected("voucher")}
                >
                  {t("voucher")}
                </Button>
                <Button
                  variant={selected === "stamp_card" ? "primary" : "secondary"}
                  onClick={() => setSelected("stamp_card")}
                >
                  {t("stamp_card")}
                </Button>
              </div>
            </Form.Item>
          </div>
        </div>

        {/* Details Section */}
        <div className="rewards-form-section">
          <h4>{t("details")}</h4>
          <div className="rewards-inputs">
            <Form.Item name="quantity" label={t("quantity")}>
              <Slider
                defaultValue={31}
                min={1}
                max={250}
                step={1}
                tooltip={{ open: true }}
              />
            </Form.Item>

            <Form.Item
              name="summary"
              label={t("reference")}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="schemeSlug"
              label={t("which_scheme")}
              rules={[{ required: true }]}
            >
              <Select
                placeholder={t("select_schema")}
                options={schemasData?.data?.body.map((item) => ({
                  label: item.name,
                  value: item.slug,
                }))}
              />
            </Form.Item>

            {selected === "voucher" && (
              <Form.Item
                name="rewardTypeSlug"
                label={t("select_reward")}
                rules={[{ required: true }]}
              >
                <Select
                  placeholder={t("select_reward")}
                  options={rewardsData?.body?.map((item) => ({
                    label: item.name,
                    value: item.slug,
                  }))}
                />
              </Form.Item>
            )}

            <Form.Item
              layout="horizontal"
              label={t("multi_use")}
              name="multiUse"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch />
            </Form.Item>

            <div className="form-row">
              <Form.Item
                className="date-picker"
                layout="vertical"
                name="validFrom"
                label={t("from_date")}
                rules={[{ required: true }]}
              >
                <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                className="date-picker"
                layout="vertical"
                name="validUntil"
                label={t("to_date")}
                rules={[{ required: true }]}
              >
                <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
              </Form.Item>
            </div>
          </div>
        </div>

        {/* Additional Info Section */}
        <div className="rewards-form-section">
          <h4>{t("additional_info")}</h4>
          <div className="rewards-inputs">
            <Form.Item
              name="message"
              label={t("include_message")}
              rules={[{ required: true }]}
            >
              <Input.TextArea style={{ resize: "none" }} rows={8} />
            </Form.Item>

            <Form.Item name="numberOfSecrets" label={t("number_of_codes")}>
              <Slider min={1} max={50} step={1} tooltip={{ open: true }} />
            </Form.Item>

            <div className="form-row">
              <Form.Item
                className="date-picker"
                layout="vertical"
                name="claimableFrom"
                label={t("claimable_from")}
                rules={[{ required: true }]}
              >
                <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                className="date-picker"
                layout="vertical"
                name="claimableUntil"
                label={t("claimable_until")}
                rules={[{ required: true }]}
              >
                <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
              </Form.Item>
            </div>

            <Form.Item
              rules={[{ required: true }]}
              label={t("max_claims_per_user")}
              name="maxClaimsPerUser"
            >
              <InputNumber min={1} max={100} />
            </Form.Item>

            <Form.Item
              required
              label={t("max_claims_per_code")}
              name="maxClaimsPerCode"
            >
              <InputNumber min={1} max={100} />
            </Form.Item>

            <Form.Item name="notes" label={t("notes")}>
              <Input.TextArea style={{ resize: "none" }} rows={8} />
            </Form.Item>

            <Form.Item name="tags" label={t("tags")}>
              <TagsInputItem tags={tags} onTagsChange={handleTagsChange} />
            </Form.Item>
          </div>
        </div>

        <Form.Item className="rewards-form-btn">
          <AntButton
            style={{ border: "1px solid #F11E69" }}
            className="btn primary"
            htmlType="submit"
            type="primary"
            loading={isLoading}
          >
            {t("submit")}
          </AntButton>
        </Form.Item>
      </Form>
    </ContentLayout>
  );
}
